<template>
  <form class="topup-form" @submit.prevent="submit">
    <div class="topup-form-frame">
      <h1>{{ $t('component.contactsEdit.header') }}</h1>
      <div class="contact-edit-person">
        <div class="contact-form-container">
          <div class="contact-form-item contact-phone">
            <label for="phone">{{ $t('component.contacts.form.labelPhone') }}:</label>
            <input type="text"
                   :value="contact.msisdn | phone"
                   id="phone"
                   disabled="disabled">
          </div>
          <div class="contact-form-item contact-name">
            <label for="name">{{ $t('component.contacts.form.labelName') }}:</label>
            <input type="text"
                   v-model.trim="form.description"
                   maxlength="30"
                   id="name">
            <div class="contact-form-item-error phone-error" v-if="isNameEmpty">
              <ul>
                <li>{{ $t('component.contacts.form.errorName') }}</li>
              </ul>
            </div>
          </div>
        </div>
        <el-button type="danger"
                   plain
                   size="small"
                   @click="removeContactSubmit"
                   icon="el-icon-delete">
          {{ $t('component.contactsEdit.contactButtonDelete') }}
        </el-button>
      </div>
      <div class="contact-edit-autotopup">
        <h3>{{ $t('component.contactsEdit.autotopupHeader') }}</h3>
        <div v-if="isAutotopupSet" class="topup-form-fields fields-inline">
          <section class="topup-form-field-zone">
            <div class="topup-form-field-content">
              <div class="line">
                <div>{{ $t('component.autotopup.stage1.fieldAmountLabel1') }}</div>
                <div class="topup-form-field narrow-field">
                  <div class="topup-form-field-container" :class="{error: isTopupAmountError}">
                    <input type="text"
                           :aria-label="$t('component.autotopup.stage1.fieldAmountLabel1')"
                           id="form-amount-period"
                           v-model="form.amount"
                           @change="formatAmount"/>
                  </div>
                </div>
                <div>{{ $t('component.autotopup.stage1.fieldAmountLabel2') }}</div>
              </div>
            </div>
            <div class="topup-form-field-errors" v-if="formErrors && isTopupAmountError">
              <ul>
                <li>{{ $t('component.autotopup.stage1.errorAmountRequired') }}</li>
              </ul>
            </div>
          </section>
          <section class="topup-form-field-zone" v-if="isConditionPeriodSet">
            <div class="topup-form-field-content">
              <div class="line">
                <div>{{ $t('component.autotopup.stage1.fieldPeriodLabel1') }}</div>
                <div class="topup-form-field narrow-field">
                  <div class="topup-form-field-container" :class="{error: isTopupPeriodError}">
                    <input type="text"
                           :aria-label="$t('component.autotopup.stage1.fieldPeriodLabel1')"
                           id="form-period"
                           v-model="form.period"
                           @change="formatPeriod"/>
                  </div>
                </div>
                <div>{{ $t('component.autotopup.stage1.fieldPeriodLabel2') }}</div>
              </div>
              <div class="line">
                <div class="topup-form-field narrow-field">
                  <div class="topup-form-field-container" :class="{error: formErrors && isNextTopupDayError}">
                    <el-date-picker
                      v-model="form.nextTopupDay"
                      :picker-options="pickerOptions"
                      type="date"
                      :placeholder="$t('component.autotopup.stage1.fieldPeriodPlaceholder')">
                    </el-date-picker>
                  </div>
                </div>
              </div>
            </div>
            <div class="topup-form-field-errors" v-if="formErrors && (isTopupPeriodError || isNextTopupDayError)">
              <ul>
                <li v-if="isTopupPeriodError">{{ $t('component.autotopup.stage1.errorPeriodRequired') }}</li>
                <li v-if="isNextTopupDayError">{{ $t('component.autotopup.stage1.errorNextTopupDayRequired') }}</li>
              </ul>
            </div>
          </section>
          <section class="topup-form-field-zone" v-if="isConditionDaySet">
            <div class="topup-form-field-content">
              <div class="line">
                <div>{{ $t('component.autotopup.stage1.fieldTopupDayLabel1') }}</div>
                <div class="topup-form-field narrow-field">
                  <div class="topup-form-field-container">
                    <select v-model="form.topupDay">
                      <option :value="value" v-for="value in topupDaysList" :key="value">{{ value }}</option>
                    </select>
                  </div>
                </div>
                <div>{{ $t('component.autotopup.stage1.fieldTopupDayLabel2') }}</div>
              </div>
            </div>
          </section>
          <h3>{{ $t('component.contactsEdit.paymentMethodHeader') }}</h3>
          <section>
            <el-button :type="getPaymentMethodTypeCardButtonType()"
                       :plain="!isPaymentMethodCard"
                       :disabled="!isUserPaymentTypeCardSet || isPaymentMethodCard"
                       @click="setPaymentMethod(PAYMENT_METHOD_TYPE.creditCard)"
                       :title="!isUserPaymentTypeCardSet || isPaymentMethodCard ? '' : $t('component.contactsEdit.a11y.buttonPaymentTypeCard')">
              {{ $t('component.contactsEdit.buttonPaymentTypeCard') }}
            </el-button>
            <el-button :type="getPaymentMethodTypeBlikButtonType()"
                       :plain="!isPaymentMethodBlik"
                       :disabled="!isUserPaymentTypeBlikSet || isPaymentMethodBlik"
                       @click="setPaymentMethod(PAYMENT_METHOD_TYPE.blikRecurring)"
                       :title="!isUserPaymentTypeBlikSet || isPaymentMethodBlik ? '' : $t('component.contactsEdit.a11y.buttonPaymentTypeBlik')">
              {{ $t('component.contactsEdit.buttonPaymentTypeBlik') }}
            </el-button>
          </section>
        </div>
        <el-button type="danger" plain
                   v-if="isAutotopupSet"
                   size="small"
                   @click="removeAutotopupSubmit"
                   icon="el-icon-delete">
          {{ $t('component.contactsEdit.autotopupButtonDelete') }}
        </el-button>
        <el-button type="success" plain
                   v-if="!isAutotopupSet"
                   size="small"
                   @click="addAutotopup"
                   icon="el-icon-plus"
                   :title="$t('component.contactsEdit.a11y.autotopupButtonAdd')">
          {{ $t('component.contactsEdit.autotopupButtonAdd') }}
        </el-button>
      </div>
    </div>
    <div class="topup-form-buttons">
      <button id="go-back-contacts"
              type="button"
              aria-label="cancel"
              @click="goBack"
              :title="$t('component.contactsEdit.a11y.buttonBack')">
        ‹ {{ $t('component.contactsEdit.buttonBack') }}
      </button>
      <button type="submit"
              aria-label="save"
              :disabled="isLoading"
              id="submit-contact-edit"
              :title="$t('component.contactsEdit.a11y.buttonNext')">
        {{ $t('component.contactsEdit.buttonNext') }} ›
      </button>
    </div>
  </form>
</template>

<script>
import ContactsEditMixin from '@/common/mixins/Contacts/Edit.mixin'

export default {
  name: 'ContactsEdit',
  mixins: [ContactsEditMixin]
}
</script>

<style src="../../assets/scss/contacts.scss" lang="scss"></style>
